/**
 * Automatically Generated with Rails
 * see {@link lib_development/generators/user_roles/user_roles_generator.rb} for details
 * run `rails g user_roles -f` to regenerate
 */

import * as React from 'react';

/**
 * All the possible roles that can be used within the application
 */
export type UserRolesKeys =
  | 'company_manager'
  | 'user_manager'
  | 'permissions_manager'
  | 'candidate_manager'
  | 'careers_site_manager'
  | 'talent_pipeline_manager'
  | 'company_teams_manager'
  | 'calendar_manager'
  | 'requisition_manager'
  | 'requisition_creator'
  | 'requisition_viewer'
  | 'draft_job_creator'
  | 'job_publisher'
  | 'job_manager'
  | 'archive_manager'
  | 'job_remover'
  | 'marketing_manager'
  | 'interview_attendee'
  | 'group_assessment_manager'
  | 'interview_manager'
  | 'sensitive_answer_viewer'
  | 'background_check_viewer'
  | 'background_check_creator'
  | 'offer_manager'
  | 'comment_viewer'
  | 'comment_manager'
  | 'email_and_sms_manager'
  | 'onboarding_manager'
  | 'reporting_manager'
  | 'insight_manager'
  | 'referral_manager';

/**
 * The shape of our UserRoles object, narrowed down to the available keys and values only.
 */
export interface IUserRoles {
  company_manager: boolean;
  user_manager: boolean;
  permissions_manager: boolean;
  candidate_manager: boolean;
  careers_site_manager: boolean;
  talent_pipeline_manager: boolean;
  company_teams_manager: boolean;
  calendar_manager: boolean;
  requisition_manager: boolean;
  requisition_creator: boolean;
  requisition_viewer: boolean;
  draft_job_creator: boolean;
  job_publisher: boolean;
  job_manager: boolean;
  archive_manager: boolean;
  job_remover: boolean;
  marketing_manager: boolean;
  interview_attendee: boolean;
  group_assessment_manager: boolean;
  interview_manager: boolean;
  sensitive_answer_viewer: boolean;
  background_check_viewer: boolean;
  background_check_creator: boolean;
  offer_manager: boolean;
  comment_viewer: boolean;
  comment_manager: boolean;
  email_and_sms_manager: boolean;
  onboarding_manager: boolean;
  reporting_manager: boolean;
  insight_manager: boolean;
  referral_manager: boolean;
}

/**
 * Initial value for all user roles.
 *
 */
export const userRolesInitialValues: IUserRoles = {
company_manager: false,
user_manager: false,
permissions_manager: false,
candidate_manager: false,
careers_site_manager: false,
talent_pipeline_manager: false,
company_teams_manager: false,
calendar_manager: false,
requisition_manager: false,
requisition_creator: false,
requisition_viewer: false,
draft_job_creator: false,
job_publisher: false,
job_manager: false,
archive_manager: false,
job_remover: false,
marketing_manager: false,
interview_attendee: false,
group_assessment_manager: false,
interview_manager: false,
sensitive_answer_viewer: false,
background_check_viewer: false,
background_check_creator: false,
offer_manager: false,
comment_viewer: false,
comment_manager: false,
email_and_sms_manager: false,
onboarding_manager: false,
reporting_manager: false,
insight_manager: false,
referral_manager: false,
}

const UserRolesContext = React.createContext<IUserRoles>(userRolesInitialValues);

export const useUserRoles = () => {
  const userRoles = React.useContext(UserRolesContext);

  return React.useMemo(
    () => ({
            isCompanyManager: userRoles.company_manager,
            isUserManager: userRoles.user_manager,
            isPermissionsManager: userRoles.permissions_manager,
            isCandidateManager: userRoles.candidate_manager,
            isCareersSiteManager: userRoles.careers_site_manager,
            isTalentPipelineManager: userRoles.talent_pipeline_manager,
            isCompanyTeamsManager: userRoles.company_teams_manager,
            isCalendarManager: userRoles.calendar_manager,
            isRequisitionManager: userRoles.requisition_manager,
            isRequisitionCreator: userRoles.requisition_creator,
            isRequisitionViewer: userRoles.requisition_viewer,
            isDraftJobCreator: userRoles.draft_job_creator,
            isJobPublisher: userRoles.job_publisher,
            isJobManager: userRoles.job_manager,
            isArchiveManager: userRoles.archive_manager,
            isJobRemover: userRoles.job_remover,
            isMarketingManager: userRoles.marketing_manager,
            isInterviewAttendee: userRoles.interview_attendee,
            isGroupAssessmentManager: userRoles.group_assessment_manager,
            isInterviewManager: userRoles.interview_manager,
            isSensitiveAnswerViewer: userRoles.sensitive_answer_viewer,
            isBackgroundCheckViewer: userRoles.background_check_viewer,
            isBackgroundCheckCreator: userRoles.background_check_creator,
            isOfferManager: userRoles.offer_manager,
            isCommentViewer: userRoles.comment_viewer,
            isCommentManager: userRoles.comment_manager,
            isEmailAndSmsManager: userRoles.email_and_sms_manager,
            isOnboardingManager: userRoles.onboarding_manager,
            isReportingManager: userRoles.reporting_manager,
            isInsightManager: userRoles.insight_manager,
            isReferralManager: userRoles.referral_manager,
          }),
    [userRoles],
  );
};

interface IUserRolesProviderProps {
  userRoles: IUserRoles;
}

export function UserRolesProvider({
    userRoles,
    children,
  }: React.PropsWithChildren<IUserRolesProviderProps>) {
  return (
    <UserRolesContext.Provider
      value={{
        ...userRolesInitialValues,
        ...userRoles,
      }}
    >
      {children}
    </UserRolesContext.Provider>
  );
}
